import ReplayIcon from '@mui/icons-material/Replay'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import CircularProgress from '@mui/material/CircularProgress'
import { useMutation } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import type { FC } from 'react'
import { useState } from 'react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CustomButton from '@/components/inputs/CustomButton'
import { useAlertContext } from '@/contexts/AlertContext'
import type { ActivationGroup } from '@/features/activationGroup/types'
import type { BidType, MarketProgram } from '@/features/bidding/constants'
import { Status } from '@/features/bidding/constants'
import { saveExtendedBids } from '@/features/bidding/endpoints/bids'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { extractServerErrorMessage } from '@/features/bidding/utils/extractServerErrorMessage'
import { getDraftOrOfferedBidsMatchingActivationGroups } from '@/features/bidding/utils/getDraftOrOfferedBidsMatchingActivationGroups'
import { getStateOfMarket, MarketState } from '@/features/bidding/utils/getStateOfMarket'

export enum BidFormStatus {
  CONFIRMING,
  SENDING,
  SEND_FAILED,
}

type Props = {
  bids: ExtendedBid[]
  eligibleActivationGroups: ActivationGroup[]
}

const BidConfirmSendButton: FC<Props> = ({ bids, eligibleActivationGroups }) => {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const navigate = useNavigate()

  const [bidFormStatus, setBidFormStatus] = useState<BidFormStatus>(BidFormStatus.CONFIRMING)

  // Filter DRAFT or OFFERED bids matching activation groups
  const bidsMatchingActivationGroups = getDraftOrOfferedBidsMatchingActivationGroups(bids, eligibleActivationGroups)

  const { mutate: mutateSaveBids } = useMutation({
    mutationFn: async () => {
      if (bidsMatchingActivationGroups.length === 0) return

      setBidFormStatus(BidFormStatus.SENDING)
      const offeredBids = bidsMatchingActivationGroups.map((bid) => ({ ...bid, status: Status.OFFERED }))
      await saveExtendedBids(offeredBids)
    },
    onError: (error) => {
      setBidFormStatus(BidFormStatus.SEND_FAILED)
      const serverMessage = extractServerErrorMessage(error)
      const message = serverMessage
        ? `${t('bidding.create_bid.sending_failed.text')} ${t('bidding.create_bid.sending_failed.error_message_from_server')} ${serverMessage}`
        : t('bidding.create_bid.sending_failed.text')

      pushAlert({
        message: message,
        severity: 'error',
        title: t('bidding.create_bid.sending_failed.title'),
      })
    },
    onSuccess: async () => {
      navigate(`/bidding/offer/success/${bids[0].marketProgram}`, { replace: true })
    },
  })

  if (bidsMatchingActivationGroups.length === 0)
    return (
      <CustomButton
        key="sendButton"
        disabled
        data-testid="send-bid-button"
        startIcon={<SendOutlinedIcon />}
        sx={{ color: 'grey' }}
        variant={'contained'}
      >
        {t('bidding.create_bid.send')}
      </CustomButton>
    )

  switch (bidFormStatus) {
    case BidFormStatus.SENDING:
      return (
        <CustomButton
          key="sendButton"
          disabled
          data-testid="send-bid-button"
          startIcon={<CircularProgress color="inherit" size={16} />}
          sx={{ color: 'grey' }}
          variant={'contained'}
        >
          {t('bidding.create_bid.sending')}
        </CustomButton>
      )
    case BidFormStatus.SEND_FAILED:
      return (
        <CustomButton
          key="sendButton"
          data-testid="send-bid-button"
          startIcon={<ReplayIcon />}
          variant={'contained'}
          onClick={() => handleSubmit(bids[0].deliveryDay, bids[0].marketProgram, bids[0].bidType, mutateSaveBids, t)}
        >
          {t('bidding.create_bid.sending_failed.retry')}
        </CustomButton>
      )
    default:
      return (
        <CustomButton
          key="sendButton"
          data-testid="send-bid-button"
          startIcon={<SendOutlinedIcon />}
          variant={'contained'}
          onClick={() => handleSubmit(bids[0].deliveryDay, bids[0].marketProgram, bids[0].bidType, mutateSaveBids, t)}
        >
          {t('bidding.create_bid.send')}
        </CustomButton>
      )
  }
}

const handleSubmit = (
  deliveryDay: MarketDate,
  marketProgram: MarketProgram,
  bidType: BidType,
  mutateSaveBids: () => void,
  t: TFunction,
) => {
  if (
    getStateOfMarket(deliveryDay, marketProgram, bidType) === MarketState.CLOSED &&
    !confirm(t('bidding.market_closed.confirmation_message'))
  ) {
    return
  }

  mutateSaveBids()
}

export default BidConfirmSendButton
